/**
 * Blank-input guards — whitespace-only values never reach the backend.
 *
 * The browser's native `required` check treats "   " as filled in, so on its own it lets a
 * whitespace-only value through. Two layers close that gap:
 *  - `installBlankFieldGuard()` (called once in main.tsx) blocks the submit of any form whose
 *    `required` text field / textarea holds only spaces, with the native "fill in" bubble.
 *  - `trimStrings()` / `trimFormData()` run on every request body in api/client.ts, so
 *    leading/trailing spaces are never sent and a blank optional field goes out as "".
 *
 * Password fields are never trimmed — a space in a password is significant.
 *
 * Screens that submit from a plain button (no <form>) use `isBlank` to disable the button.
 */

/** True for "", null/undefined, or a string of only whitespace. */
export const isBlank = (v: string | null | undefined): boolean => !v || v.trim() === "";

const KEEP_AS_TYPED = /password/i;

const BLANK_MESSAGE = "This field can't be empty or only spaces.";

const TEXT_INPUT_TYPES = new Set(["text", "search", "email", "tel", "url", "password"]);

/** Deep-trims every string in a JSON request body (plain objects + arrays), skipping passwords. */
export function trimStrings<T>(value: T): T {
  if (typeof value === "string") return value.trim() as T;
  if (Array.isArray(value)) return value.map(trimStrings) as T;
  if (value !== null && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, KEEP_AS_TYPED.test(k) ? v : trimStrings(v)]),
    ) as T;
  }
  return value;
}

/** Multipart twin of `trimStrings`: trims text parts, passes files through untouched. */
export function trimFormData(form: FormData): FormData {
  const out = new FormData();
  form.forEach((v, k) => out.append(k, typeof v === "string" && !KEEP_AS_TYPED.test(k) ? v.trim() : v));
  return out;
}

function isBlankRequired(el: Element): el is HTMLInputElement | HTMLTextAreaElement {
  const isText =
    el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(el.type));
  return isText && el.required && !el.disabled && el.value.trim() === "";
}

/**
 * App-wide submit guard. Listens in the capture phase on `document`, which runs before React's
 * root listener — so when a required field is blank, the form's onSubmit (and its API call)
 * never fires. Forms marked `noValidate` run their own validation and are left alone.
 */
export function installBlankFieldGuard(): void {
  document.addEventListener(
    "submit",
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || form.noValidate) return;
      const blank = Array.from(form.elements).find(isBlankRequired);
      if (!blank) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      blank.setCustomValidity(BLANK_MESSAGE);
      blank.reportValidity();
      blank.addEventListener("input", () => blank.setCustomValidity(""), { once: true });
    },
    true,
  );
}
